<template>
  <v-content>
    <v-container>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">feedback</v-icon>
          PQRS interno
          <v-chip class="ml-3 mr-2" color="primary" dark>{{ filtered.length }} registros</v-chip>
          <v-chip v-if="sinAsignarCount > 0" class="mr-2" color="deep-orange" dark>
            {{ sinAsignarCount }} sin asignar
          </v-chip>
          <v-chip v-if="vencidosCount > 0" class="mr-2" color="error" dark>
            {{ vencidosCount }} vencidos
          </v-chip>
          <v-spacer/>
          <download-excel
            v-if="filtered.length > 0"
            class="v-btn v-btn--contained theme--light v-size--default success"
            :data="excelData"
            :fields="json_fields"
            worksheet="PQRS interno"
            :name="`pqrs_interno_${$moment().format('YYYY-MM-DD')}.xls`"
          >
            Exportar excel
          </download-excel>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-alert type="info" dense text>
            Son las peticiones, quejas y reclamos que cargan los empleados desde la app.
            Llegan sin asignar: hay que derivarlas al area que las tiene que resolver.
          </v-alert>
        </v-card-text>

        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterTipo"
                :items="tipoOptions"
                label="Tipo"
                clearable
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterEstado"
                :items="estadoOptions"
                item-text="label"
                item-value="value"
                label="Estado"
                clearable
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterArea"
                :items="areaOptions"
                item-text="label"
                item-value="value"
                label="Area asignada"
                clearable
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Buscar"
                single-line
                dense
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-data-table
          :headers="headers"
          :items="filtered"
          sort-by="createdAtMs"
          sort-desc
          class="elevation-1"
          :items-per-page="10"
          no-data-text="No hay PQRS registrados"
          no-results-text="No se encontraron resultados"
        >
          <template v-slot:item.createdAtMs="{ item }">
            {{ item.createdAtMs ? $moment(item.createdAtMs).format('DD/MM/YYYY HH:mm') : '-' }}
          </template>

          <template v-slot:item.tipo="{ item }">
            <v-chip :color="tipoColor(item.tipo)" dark small>{{ item.tipo }}</v-chip>
          </template>

          <template v-slot:item.dependenciaNombre="{ item }">
            <span v-if="item.dependenciaNombre">{{ item.dependenciaNombre }}</span>
            <v-chip v-else x-small color="deep-orange" dark>Sin asignar</v-chip>
          </template>

          <template v-slot:item.estado="{ item }">
            <v-chip :color="estadoColor(item.estado)" dark small>{{ estadoLabel(item.estado) }}</v-chip>
            <v-chip v-if="item.seguimientos.length > 0" x-small class="ml-1" title="Gestiones registradas">
              <v-icon x-small left>history</v-icon>
              {{ item.seguimientos.length }}
            </v-chip>
            <v-chip
              v-if="cantidadAdjuntos(item) > 0"
              x-small
              class="ml-1"
              color="indigo lighten-5"
              title="Soportes adjuntos"
            >
              <v-icon x-small left>attach_file</v-icon>
              {{ cantidadAdjuntos(item) }}
            </v-chip>
          </template>

          <template v-slot:item.terminos="{ item }">
            <v-chip :color="terminoColor(item)" dark small>{{ terminoLabel(item) }}</v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openDetail(item)">
              <v-icon small>visibility</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Detalle y gestion -->
      <v-dialog v-model="dialog" max-width="720" scrollable>
        <v-card v-if="selected">
          <v-card-title :class="`${tipoColor(selected.tipo)} white--text`">
            <v-icon dark class="mr-2">feedback</v-icon>
            {{ selected.tipo }}
            <v-spacer/>
            <v-chip :color="terminoColor(selected)" dark small>{{ terminoLabel(selected) }}</v-chip>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-list dense>
              <v-list-item>
                <v-list-item-icon><v-icon>person</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Presentado por</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ nombreDe(selected.user) }}
                    <span v-if="selected.user && selected.user.role" class="caption grey--text">
                      ({{ selected.user.role }})
                    </span>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>label</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Dirigido a (segun el reportante)</v-list-item-subtitle>
                  <v-list-item-title>{{ selected.motivo || '-' }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>description</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Categoria</v-list-item-subtitle>
                  <v-list-item-title>{{ selected.solicitud || '-' }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon><v-icon>event</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Fecha</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ selected.createdAtMs ? $moment(selected.createdAtMs).format('dddd D [de] MMMM [de] YYYY, HH:mm') : 'Sin fecha registrada' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <div class="subtitle-2 grey--text mb-1 mt-3">Descripcion</div>
            <div class="body-1 mb-4" style="white-space: pre-wrap">{{ selected.descripcion }}</div>

            <v-divider class="mb-4"/>

            <!-- Derivacion al area responsable -->
            <div class="subtitle-1 mb-2">Area que lo atiende</div>

            <v-alert v-if="!selected.dependenciaId" type="warning" dense text class="mb-2">
              Todavia no fue derivado a ninguna area, asi que nadie lo tiene asignado.
            </v-alert>

            <div v-if="puedeAsignar" class="d-flex align-center mb-4">
              <v-select
                v-model="areaElegida"
                :items="dependenciasActivas"
                item-text="name"
                item-value="id"
                label="Derivar a"
                outlined
                dense
                hide-details
                class="mr-2"
              />
              <v-btn
                color="primary"
                depressed
                :disabled="!areaElegida || areaElegida === selected.dependenciaId || saving"
                :loading="asignando"
                @click="asignarArea"
              >
                {{ selected.dependenciaId ? 'Cambiar' : 'Derivar' }}
              </v-btn>
            </div>
            <div v-else class="body-2 mb-4">
              {{ selected.dependenciaNombre || 'Sin asignar' }}
              <span class="caption grey--text d-block">
                Tu perfil no puede derivar PQRS a un area.
              </span>
            </div>

            <v-divider class="mb-4"/>

            <!-- Respuesta vigente -->
            <template v-if="selected.respuesta">
              <div class="subtitle-2 grey--text mb-1">Respuesta al empleado</div>
              <v-alert type="success" dense text class="mb-1" style="white-space: pre-wrap">
                {{ selected.respuesta }}
              </v-alert>
              <div v-if="selected.respondidoPor" class="caption grey--text mb-4">
                Por {{ selected.respondidoPor.email }}
                <span v-if="selected.respondidoAtMs">
                  el {{ $moment(selected.respondidoAtMs).format('DD/MM/YYYY HH:mm') }}
                </span>
              </div>
            </template>

            <!-- Bitacora -->
            <div class="subtitle-1 mb-2">
              Historial de seguimiento
              <v-chip x-small class="ml-1">{{ seguimientos.length }}</v-chip>
            </div>

            <div v-if="seguimientos.length === 0" class="caption grey--text mb-4">
              Todavia no hay gestiones registradas.
            </div>

            <v-timeline v-else dense class="mb-4 pt-0">
              <v-timeline-item
                v-for="(s, i) in seguimientos"
                :key="i"
                small
                :color="estadoColor(s.estadoNuevo)"
              >
                <div class="caption grey--text">
                  {{ s.fechaMs ? $moment(s.fechaMs).format('DD/MM/YYYY HH:mm') : '' }}
                  <span v-if="s.autor"> · {{ s.autor.email }}</span>
                </div>

                <div v-if="s.estadoAnterior !== s.estadoNuevo" class="body-2">
                  <v-chip x-small :color="estadoColor(s.estadoAnterior)" dark>
                    {{ estadoLabel(s.estadoAnterior) }}
                  </v-chip>
                  <v-icon x-small class="mx-1">arrow_forward</v-icon>
                  <v-chip x-small :color="estadoColor(s.estadoNuevo)" dark>
                    {{ estadoLabel(s.estadoNuevo) }}
                  </v-chip>
                </div>

                <div v-if="s.derivadoA" class="body-2 mt-1">
                  <v-icon x-small class="mr-1">call_split</v-icon>
                  Derivado a <strong>{{ s.derivadoA }}</strong>
                </div>

                <div v-if="s.nota" class="body-2 mt-1" style="white-space: pre-wrap">
                  {{ s.nota }}
                  <v-chip v-if="s.oficial" x-small color="success" dark class="ml-1">
                    Respuesta al empleado
                  </v-chip>
                </div>

                <div class="mt-1">
                  <v-chip
                    v-for="(a, j) in (s.adjuntos || [])"
                    :key="j"
                    x-small
                    class="mr-1 mb-1"
                    color="indigo lighten-5"
                    style="cursor: pointer"
                    @click="abrirAdjunto(a)"
                  >
                    <v-icon x-small left>{{ iconoAdjunto(a) }}</v-icon>
                    {{ a.nombre }}
                    <span class="grey--text ml-1">{{ tamanoLegible(a.tamano) }}</span>
                  </v-chip>
                </div>
              </v-timeline-item>
            </v-timeline>

            <v-divider class="mb-4"/>

            <div class="subtitle-1 mb-3">Nueva gestion</div>

            <v-select
              v-model="gestion.estado"
              :items="estadoOptions"
              item-text="label"
              item-value="value"
              :disabled="!puedeCambiarEstado"
              :hint="puedeCambiarEstado ? '' : 'Tu perfil no puede cambiar el estado.'"
              :persistent-hint="!puedeCambiarEstado"
              label="Estado"
              outlined
              dense
            />

            <v-textarea
              v-model="gestion.nota"
              :disabled="!puedeGestionar && !puedeCambiarEstado"
              @input="alCambiarNota"
              label="Nota de seguimiento"
              placeholder="Que se hizo, con quien se hablo, que falta..."
              outlined
              rows="4"
              auto-grow
            />

            <v-file-input
              v-model="gestion.archivos"
              :accept="acceptAdjuntos"
              :disabled="!puedeGestionar && !puedeCambiarEstado"
              label="Soporte (opcional)"
              placeholder="Adjunta el comprobante de lo que se hizo"
              hint="Imagen, video, PDF, Word, Excel o correo. Hasta 10 MB por archivo."
              persistent-hint
              multiple
              chips
              small-chips
              show-size
              outlined
              dense
              prepend-icon="attach_file"
              class="mb-2"
            />

            <v-checkbox
              v-model="gestion.oficial"
              :disabled="!puedeResponder || !gestion.nota || !gestion.nota.trim()"
              class="mt-0"
              hide-details
            >
              <template v-slot:label>
                <div>
                  Esta nota es la respuesta para el empleado
                  <div class="caption grey--text">
                    Si no la marcas, queda como nota interna y no se ve desde la app.
                  </div>
                </div>
              </template>
            </v-checkbox>

            <v-alert v-if="requiereRespuesta" type="warning" dense text class="mt-3">
              Para marcar el PQRS como Resuelto o Cerrado tiene que haber una respuesta.
            </v-alert>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="dialog = false">Cerrar</v-btn>
            <v-btn
              color="primary"
              :disabled="!hayCambios || requiereRespuesta || saving"
              :loading="saving"
              @click="guardarGestion"
            >
              {{ subiendo ? 'Subiendo soporte...' : 'Guardar gestion' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Visor de soportes -->
      <v-dialog v-model="visor.abierto" max-width="1000" scrollable>
        <v-card v-if="visor.adjunto">
          <v-card-title class="py-2">
            <v-icon class="mr-2">{{ iconoAdjunto(visor.adjunto) }}</v-icon>
            <span class="subtitle-1 text-truncate">{{ visor.adjunto.nombre }}</span>
            <v-spacer/>
            <v-btn icon :href="visor.adjunto.url" target="_blank" rel="noopener" title="Descargar">
              <v-icon>file_download</v-icon>
            </v-btn>
            <v-btn icon @click="visor.abierto = false" title="Cerrar">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider/>

          <v-card-text class="visor-contenido">
            <img
              v-if="visor.abierto && visor.tipo === 'imagen'"
              :src="visor.adjunto.url"
              :alt="visor.adjunto.nombre"
              class="visor-media"
            />
            <video
              v-else-if="visor.abierto && visor.tipo === 'video'"
              :src="visor.adjunto.url"
              class="visor-media"
              controls
              autoplay
            />
            <iframe
              v-else-if="visor.abierto && visor.tipo === 'pdf'"
              :src="visor.adjunto.url"
              class="visor-pdf"
              frameborder="0"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-snackbar :color="snackColor" v-model="snackbar">
        {{ text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import { mapState } from 'vuex'
import { diasHabilesEntre } from '../plugins/festivos'
import {
  ACCEPT_ATTR,
  validarLote,
  subirAdjuntos,
  borrarAdjuntos,
  iconoAdjunto,
  tamanoLegible,
  tipoVisor
} from '../plugins/adjuntos'

// Termino interno para responder un PQRS del personal.
const DIAS_TERMINO = 15

export default {
  name: 'Pqrs',

  data () {
    return {
      search: '',
      filterTipo: null,
      filterEstado: null,
      filterArea: null,
      dialog: false,
      selectedId: null,
      saving: false,
      asignando: false,
      subiendo: false,
      areaElegida: null,
      snackbar: false,
      snackColor: 'success',
      text: '',
      acceptAdjuntos: ACCEPT_ATTR,
      visor: { abierto: false, adjunto: null, tipo: '' },
      gestion: {
        estado: 'NUEVO',
        nota: '',
        oficial: false,
        archivos: []
      },
      // Los valores historicos vienen con y sin tilde: la comparacion se
      // normaliza para que el filtro no dependa de eso.
      tipoOptions: ['Peticion', 'Queja', 'Reclamo', 'Sugerencia', 'Denuncia'],
      estadoOptions: [
        { value: 'NUEVO', label: 'Nuevo' },
        { value: 'EN_REVISION', label: 'En revisión' },
        { value: 'RESUELTO', label: 'Resuelto' },
        { value: 'CERRADO', label: 'Cerrado' }
      ],
      headers: [
        { text: 'Fecha', value: 'createdAtMs' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Presentado por', value: 'empleado' },
        { text: 'Categoria', value: 'solicitud' },
        { text: 'Area asignada', value: 'dependenciaNombre' },
        { text: 'Estado', value: 'estado' },
        { text: 'Terminos', value: 'terminos', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      json_fields: {
        Fecha: 'fechaTexto',
        Tipo: 'tipo',
        'Presentado por': 'empleado',
        'Dirigido a': 'motivo',
        Categoria: 'solicitud',
        'Area asignada': 'areaTexto',
        Descripcion: 'descripcion',
        Estado: 'estadoTexto',
        Respuesta: 'respuesta',
        Gestiones: 'cantidadGestiones',
        'Soportes adjuntos': 'cantidadSoportes',
        'Dias habiles transcurridos': 'diasHabiles'
      }
    }
  },

  computed: {
    ...mapState({
      pqrsRaw: state => state.pqrs.pqrs,
      dependencias: state => state.dependencia.dependencias
    }),

    puedeVerTodas () {
      return this.$store.getters.puede('pqr.verTodas')
    },
    puedeAsignar () {
      return this.$store.getters.puede('pqrsInterno.asignar')
    },
    puedeGestionar () {
      return this.$store.getters.puede('pqrsInterno.gestionar')
    },
    puedeResponder () {
      return this.$store.getters.puede('pqrsInterno.responder')
    },
    puedeCambiarEstado () {
      return this.$store.getters.puede('pqrsInterno.cambiarEstado')
    },
    misDependencias () {
      return this.$store.getters.misDependencias
    },

    dependenciasActivas () {
      return (this.dependencias || []).filter(d => d.active !== false)
    },

    // Normaliza lo que viene de Firestore y recorta por alcance. El recorte va
    // aca y no solo en `filtered` para que ningun contador ni desplegable
    // exponga datos de areas ajenas.
    visibles () {
      const items = (this.pqrsRaw || []).map(p => ({
        ...p,
        id: p.id,
        estado: p.estado || 'NUEVO',
        seguimientos: Array.isArray(p.seguimientos) ? p.seguimientos : [],
        empleado: this.nombreDe(p.user),
        createdAtMs: this.toMillis(p.createdAt),
        respondidoAtMs: this.toMillis(p.respondidoAt)
      }))

      if (this.puedeVerTodas) return items
      return items.filter(p => this.misDependencias.includes(p.dependenciaId))
    },

    filtered () {
      const q = (this.search || '').trim().toLowerCase()

      return this.visibles.filter(p => {
        if (this.filterTipo && this.normalizar(p.tipo) !== this.normalizar(this.filterTipo)) return false
        if (this.filterEstado && p.estado !== this.filterEstado) return false

        if (this.filterArea === '__sin_asignar__') {
          if (p.dependenciaId) return false
        } else if (this.filterArea && p.dependenciaId !== this.filterArea) {
          return false
        }

        if (!q) return true
        return [
          p.empleado, p.tipo, p.motivo, p.solicitud,
          p.dependenciaNombre, p.descripcion, p.respuesta
        ].some(v => (v || '').toString().toLowerCase().includes(q))
      })
    },

    areaOptions () {
      const opciones = this.dependenciasActivas.map(d => ({ value: d.id, label: d.name }))
      return [{ value: '__sin_asignar__', label: 'Sin asignar' }, ...opciones]
    },

    sinAsignarCount () {
      return this.visibles.filter(p => !p.dependenciaId).length
    },

    vencidosCount () {
      return this.filtered.filter(p => this.estaVencido(p)).length
    },

    selected () {
      if (!this.selectedId) return null
      return this.visibles.find(p => p.id === this.selectedId) || null
    },

    seguimientos () {
      if (!this.selected) return []
      return this.selected.seguimientos
        .map(s => ({ ...s, fechaMs: this.toMillis(s.fecha) }))
        .sort((a, b) => (b.fechaMs || 0) - (a.fechaMs || 0))
    },

    requiereRespuesta () {
      if (!this.selected || !this.puedeCambiarEstado) return false
      const cierraAhora = (this.gestion.estado === 'RESUELTO' || this.gestion.estado === 'CERRADO') &&
        this.gestion.estado !== this.selected.estado
      if (!cierraAhora) return false

      const yaTiene = !!(this.selected.respuesta || '').trim()
      const laDaAhora = this.puedeResponder && this.gestion.oficial && !!(this.gestion.nota || '').trim()
      return !(yaTiene || laDaAhora)
    },

    hayCambios () {
      if (!this.selected) return false
      const cambioEstado = this.puedeCambiarEstado && this.gestion.estado !== this.selected.estado
      return cambioEstado ||
        !!(this.gestion.nota || '').trim() ||
        (this.gestion.archivos || []).length > 0
    },

    excelData () {
      return this.filtered.map(p => ({
        ...p,
        fechaTexto: p.createdAtMs ? this.$moment(p.createdAtMs).format('DD/MM/YYYY HH:mm') : '',
        areaTexto: p.dependenciaNombre || 'Sin asignar',
        estadoTexto: this.estadoLabel(p.estado),
        respuesta: this.escapar(p.respuesta || ''),
        descripcion: this.escapar(p.descripcion || ''),
        cantidadGestiones: p.seguimientos.length,
        cantidadSoportes: this.cantidadAdjuntos(p),
        diasHabiles: this.diasHabilesTranscurridos(p)
      }))
    }
  },

  mounted () {
    this.suscribir()
    this.$store.dispatch('dependencia/bindDependencias')
  },

  methods: {
    iconoAdjunto,
    tamanoLegible,

    suscribir () {
      const opciones = this.puedeVerTodas
        ? { todas: true }
        : { dependencias: this.misDependencias }
      return this.$store.dispatch('pqrs/bindPqrs', opciones)
        .catch(() => {
          this.notify('No se pudieron cargar los PQRS. Revisá tus permisos o la conexión.', 'error')
        })
    },

    nombreDe (user) {
      if (!user) return 'Sin usuario'
      const nombre = [user.first_name, user.last_name].filter(Boolean).join(' ')
      return nombre || user.email || 'Sin usuario'
    },

    normalizar (valor) {
      const tildes = { 'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u' }
      return (valor || '').toLowerCase().replace(/[áéíóú]/g, c => tildes[c])
    },

    // vue-json-excel arma el .xls como tabla HTML y no escapa nada.
    escapar (valor) {
      return (valor || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    },

    toMillis (value) {
      if (!value) return null
      if (typeof value.toDate === 'function') return value.toDate().getTime()
      if (value instanceof Date) return value.getTime()
      if (value.seconds) return value.seconds * 1000
      return null
    },

    cantidadAdjuntos (item) {
      return (item.seguimientos || []).reduce(
        (total, s) => total + ((s.adjuntos && s.adjuntos.length) || 0), 0
      )
    },

    diasHabilesTranscurridos (item) {
      if (!item.createdAtMs) return 0
      return diasHabilesEntre(new Date(item.createdAtMs), new Date())
    },

    estaCerrado (item) {
      return item.estado === 'RESUELTO' || item.estado === 'CERRADO'
    },

    estaVencido (item) {
      return !this.estaCerrado(item) && this.diasHabilesTranscurridos(item) > DIAS_TERMINO
    },

    terminoLabel (item) {
      if (this.estaCerrado(item)) return 'Atendido'
      if (!item.createdAtMs) return 'Sin fecha'
      const dias = this.diasHabilesTranscurridos(item)
      if (dias > DIAS_TERMINO) return `Vencido (${dias} dias)`
      return `${dias} de ${DIAS_TERMINO} dias`
    },

    terminoColor (item) {
      if (this.estaCerrado(item)) return 'grey'
      if (!item.createdAtMs) return 'blue-grey lighten-3'
      const dias = this.diasHabilesTranscurridos(item)
      if (dias > DIAS_TERMINO) return 'red darken-2'
      if (dias >= 10) return 'orange darken-2'
      return 'green'
    },

    tipoColor (tipo) {
      const colores = {
        peticion: 'blue',
        queja: 'red',
        reclamo: 'purple',
        sugerencia: 'green',
        denuncia: 'orange darken-1'
      }
      return colores[this.normalizar(tipo)] || 'grey'
    },

    estadoLabel (estado) {
      const found = this.estadoOptions.find(e => e.value === estado)
      return found ? found.label : 'Nuevo'
    },

    estadoColor (estado) {
      const colores = {
        NUEVO: 'blue-grey',
        EN_REVISION: 'orange darken-2',
        RESUELTO: 'green',
        CERRADO: 'grey darken-1'
      }
      return colores[estado] || 'blue-grey'
    },

    alCambiarNota () {
      if (!(this.gestion.nota || '').trim()) {
        this.gestion.oficial = false
      }
    },

    abrirAdjunto (adjunto) {
      const tipo = tipoVisor(adjunto)
      if (tipo === 'descarga') {
        window.open(adjunto.url, '_blank', 'noopener')
        return
      }
      this.visor = { abierto: true, adjunto, tipo }
    },

    openDetail (item) {
      this.selectedId = item.id
      this.areaElegida = item.dependenciaId || null
      this.gestion = { estado: item.estado || 'NUEVO', nota: '', oficial: false, archivos: [] }
      this.dialog = true
    },

    // Derivar deja constancia en la bitacora: es una decision que hay que poder
    // auditar igual que una respuesta.
    async asignarArea () {
      if (this.asignando || !this.selected || !this.areaElegida) return
      const area = this.dependenciasActivas.find(d => d.id === this.areaElegida)
      if (!area) return

      this.asignando = true
      const admin = this.$store.getters.user
      const autor = admin ? { id: admin.id, email: admin.email } : null

      try {
        await this.$store.dispatch('pqrs/agregarSeguimiento', {
          id: this.selected.id,
          entrada: {
            fecha: new Date(),
            autor,
            estadoAnterior: this.selected.estado,
            estadoNuevo: this.selected.estado,
            nota: '',
            oficial: false,
            derivadoA: area.name,
            adjuntos: []
          },
          data: { dependenciaId: area.id, dependenciaNombre: area.name }
        })
        this.notify(`Derivado a ${area.name}`, 'success')
      } catch (e) {
        this.notify('No se pudo derivar el PQRS', 'error')
      } finally {
        this.asignando = false
      }
    },

    async guardarGestion () {
      if (this.saving || !this.hayCambios || this.requiereRespuesta) return
      if (!this.puedeGestionar && !this.puedeCambiarEstado) {
        this.notify('Tu perfil no puede registrar gestiones en este PQRS', 'error')
        return
      }

      const archivos = this.gestion.archivos || []
      const errorArchivos = validarLote(archivos)
      if (errorArchivos) {
        this.notify(errorArchivos, 'error')
        return
      }

      this.saving = true

      // Se congela todo antes del primer await: `selected` se resuelve contra la
      // coleccion en vivo y puede cambiar mientras se suben los adjuntos.
      const id = this.selected.id
      const estadoActual = this.selected.estado
      const admin = this.$store.getters.user
      const autor = admin ? { id: admin.id, email: admin.email } : null
      const nota = (this.gestion.nota || '').trim()
      const esOficial = this.puedeResponder && this.gestion.oficial && !!nota
      const fecha = new Date()
      const estadoDestino = this.puedeCambiarEstado ? this.gestion.estado : estadoActual

      let adjuntos = []
      try {
        if (archivos.length > 0) {
          this.subiendo = true
          adjuntos = await subirAdjuntos(id, archivos, 'pqrs_interno')
          this.subiendo = false
        }

        const data = {}
        if (this.puedeCambiarEstado) data.estado = estadoDestino
        if (esOficial) {
          data.respuesta = nota
          data.respuestaAdjuntos = adjuntos
          data.respondidoPor = autor
          data.respondidoAt = fecha
        }

        await this.$store.dispatch('pqrs/agregarSeguimiento', {
          id,
          entrada: {
            fecha,
            autor,
            estadoAnterior: estadoActual,
            estadoNuevo: estadoDestino,
            nota,
            oficial: esOficial,
            adjuntos
          },
          data
        })

        this.notify('Gestion registrada correctamente', 'success')
        this.gestion = { estado: estadoDestino, nota: '', oficial: false, archivos: [] }
      } catch (e) {
        // Si la escritura fallo, los archivos ya subidos quedarian sin referencia.
        if (adjuntos.length > 0) borrarAdjuntos(adjuntos)
        this.notify(
          this.subiendo
            ? 'Error al subir el soporte. Revisá el archivo y los permisos de Storage.'
            : 'Error al registrar la gestion',
          'error'
        )
      } finally {
        this.subiendo = false
        this.saving = false
      }
    },

    notify (message, color) {
      this.text = message
      this.snackColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.visor-contenido {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1e1e1e;
  padding: 12px;
  min-height: 320px;
}

.visor-media {
  max-width: 100%;
  max-height: 72vh;
  object-fit: contain;
}

.visor-pdf {
  width: 100%;
  height: 72vh;
  background: #fff;
}
</style>
